// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import ht from './locales/ht.json';

// Supported languages configuration
// Note: Haitian Creole (ht) is available but not yet enabled in the UI
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

// Local storage key for language preference (used as cache for faster initial load)
// The source of truth is stored in Medplum on the Practitioner profile
const LANGUAGE_STORAGE_KEY = 'carepoint-language';

// Get cached language from localStorage (for initial fast load before Medplum profile is fetched)
export function getCachedLanguage(): LanguageCode {
  const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (saved && SUPPORTED_LANGUAGES.some((l) => l.code === saved)) {
    return saved as LanguageCode;
  }
  return 'en';
}

// Cache language preference to localStorage (for faster loading on next visit)
// Note: The source of truth is Medplum, this is just a cache
export function cacheLanguage(lang: LanguageCode): void {
  localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
}

// Legacy alias for backward compatibility
export const getSavedLanguage = getCachedLanguage;
export const saveLanguage = cacheLanguage;

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      fr: { translation: fr },
      ht: { translation: ht },
    },
    lng: getSavedLanguage(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

// Change language and update cache
// Note: This should only be called when the language preference is also being saved to Medplum
export function changeLanguage(lang: LanguageCode): void {
  cacheLanguage(lang); // Update cache for next page load
  i18n.changeLanguage(lang);
}

// Apply language from Medplum user preferences (called on login/page load)
// This syncs the app language with what's stored in Medplum (source of truth)
export function applyLanguageFromPreferences(lang: string | undefined): void {
  if (lang && SUPPORTED_LANGUAGES.some((l) => l.code === lang)) {
    const languageCode = lang as LanguageCode;
    cacheLanguage(languageCode); // Cache for faster loading on next visit
    i18n.changeLanguage(languageCode);
  }
}

export default i18n;
